import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { InputNumber } from 'primeng/inputnumber';

import { MOEDA_CONFIG, Moeda, MoedaConfig } from '../../../../../../enums/moeda.enum';
import { OperarVendaItemBuscaComponent } from '../operar-venda-item-busca/operar-venda-item-busca.component';

/** Item pronto para virar linha do carrinho. Quem grava é o PAI. */
export interface LancamentoVenda {
  item: any;
  quantidade: number;
  valorUnitario: number;
}

/**
 * De onde veio a linha que está sendo editada.
 *
 * `linha` é OPACA de propósito (`unknown`): este componente não sabe o que é um
 * item de carrinho e não deve saber — ele só guarda o valor para devolvê-lo
 * intacto ao pai se a edição for abandonada.
 */
export interface OrigemEdicao {
  linha: unknown;
  indice: number;
}

/** Pedido de edição feito pelo pai (botão Editar de uma linha do carrinho). */
export interface PedidoEdicao {
  produto: any;
  quantidade: number;
  preco: number;
  origem: OrigemEdicao;
}

/**
 * Linha de lançamento da aba "Nova venda" — Item | Qtd. | Preço unit. | Subtotal | +
 *
 * Modelo "compor e confirmar" do AvatiERP: escolher um item não o manda ao
 * carrinho; ele fica aqui até o operador confirmar (Enter na quantidade ou +).
 *
 * FRONTEIRA:
 *   • aqui  : item em composição, quantidade, preço efetivo e pendência de
 *             edição, com as regras de restauração;
 *   • no pai: carrinho, soma de item repetido, modal de preço e gravação.
 * Este componente não conhece o carrinho — PEDE (`lancar`,
 * `devolverAoCarrinho`) e o pai decide.
 *
 * ⚠️ Preço unit. e Subtotal são CAIXAS DE LEITURA, não inputs: é o que preserva
 * a regra de fdf285f9 (o funcionário não altera o preço de venda) ao copiar o
 * layout do ERP. O único campo digitável é a quantidade.
 */
@Component({
  selector: 'app-operar-venda-linha',
  templateUrl: './operar-venda-linha.component.html',
  styleUrls: ['./operar-venda-linha.component.scss'],
})
export class OperarVendaLinhaComponent {
  /**
   * Moeda do CLIENTE (símbolo/casas/locale). Vem do pai, NUNCA da sessão, que
   * responde pela empresa do CONTADOR — ver `app-operar-venda-item-busca`.
   */
  @Input() cfgMoeda: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];

  /** Bloqueia a faixa enquanto a venda está sendo gravada (`salvando` no pai). */
  @Input() disabled = false;

  /** Item pronto para entrar no carrinho (confirmação ou leitura de código). */
  @Output() lancar = new EventEmitter<LancamentoVenda>();

  /**
   * Item escolhido que NÃO tem preço cadastrado.
   *
   * Não entra na composição: a nota não pode nascer com item a zero, e o preço
   * só pode ser informado no modal, onde existe o gate que recusa zero. Quem
   * abre o modal é o pai.
   */
  @Output() precoAusente = new EventEmitter<any>();

  /** Devolve ao carrinho a linha cuja edição foi abandonada. */
  @Output() devolverAoCarrinho = new EventEmitter<OrigemEdicao>();

  /** Repasses do campo de busca — o pai é quem abre os diálogos. */
  @Output() abrirLista = new EventEmitter<void>();
  @Output() criarItem = new EventEmitter<void>();

  @ViewChild('qtdRef') private qtdRef?: InputNumber;
  @ViewChild(OperarVendaItemBuscaComponent) private busca?: OperarVendaItemBuscaComponent;

  /** Item CRU em composição. `null` = faixa vazia. */
  item: any = null;

  /** Quantidade da composição. Único campo editável da faixa. */
  qtd = 1;

  /**
   * Preço efetivo, quando NÃO é o do cadastro do item.
   *
   * Existe por causa da EDIÇÃO: uma linha cujo preço foi informado no modal
   * (item sem preço cadastrado) tem `produto.precoDeVenda = 0`. Ao voltar para
   * a composição, ler o cadastro mostraria 0,00 e o item seria relançado de
   * graça. `null` = usar o preço do cadastro.
   */
  private precoOverride: number | null = null;

  /** Linha retirada do carrinho para edição. `null` = lançamento novo. */
  private edicaoPendente: OrigemEdicao | null = null;

  /** Há edição em curso? O template troca o ícone e o rótulo do botão. */
  get editando(): boolean {
    return this.edicaoPendente !== null;
  }

  /** Preço unitário exibido — do item, nunca digitado. */
  get precoUnitario(): number {
    if (this.precoOverride !== null) return this.precoOverride;
    return Number(this.item?.precoDeVenda ?? 0);
  }

  /** Subtotal ao vivo (quantidade x preço), recalculado a cada tecla. */
  get subtotal(): number {
    return (Number(this.qtd) || 0) * this.precoUnitario;
  }

  // ===========================================================================
  // API pública (o pai chama por ViewChild)
  // ===========================================================================

  /**
   * Carrega na faixa uma linha que veio do carrinho (botão Editar).
   *
   * O pai já retirou a linha do carrinho antes de chamar; `origem` é o que
   * permite devolvê-la se a edição for abandonada.
   */
  editar(pedido: PedidoEdicao): void {
    this.carregar(pedido.produto, pedido.quantidade, pedido.preco);
    this.edicaoPendente = pedido.origem;
  }

  /**
   * Esvazia a composição — ÚNICO ponto de limpeza.
   *
   * `restaurarEdicao = true` (padrão) devolve ao carrinho a linha que estava
   * sendo editada: é o comportamento de CANCELAR, e é o que impede que o `×`
   * vire um "apagar" para quem clicou em "editar".
   *
   * O pai passa `false` em dois casos, e só neles: ao concluir um lançamento
   * (a linha nova substitui a antiga — restaurar criaria duplicata) e ao zerar
   * o formulário inteiro (o carrinho todo vai embora; restaurar deixaria um
   * item fantasma do cliente anterior).
   */
  limparComposicao(restaurarEdicao = true): void {
    const pendente = this.edicaoPendente;
    this.edicaoPendente = null;

    if (restaurarEdicao && pendente) {
      this.devolverAoCarrinho.emit(pendente);
    }

    this.item = null;
    this.qtd = 1;
    this.precoOverride = null;
    // Silencioso: `limparSelecao()` não reemite `(selecaoLimpa)`, senão o
    // handler abaixo voltaria para cá em laço.
    this.busca?.limparSelecao();
  }

  /** Devolve o foco ao campo Item e reabre a lista prévia (lançar em rajada). */
  focar(): void {
    this.busca?.focarCampo();
  }

  // ===========================================================================
  // Eventos do campo de busca
  // ===========================================================================

  /**
   * Item escolhido no dropdown.
   *
   * Com preço → carrega na faixa. Sem preço → nem faixa, nem carrinho: vai para
   * o modal (o pai abre), e a faixa fica limpa para não mostrar o nome de um
   * item que não está em composição.
   */
  onItemEscolhido(produto: any): void {
    if (!produto?.id) return;

    // Escolher OUTRO item abandona a edição em curso: a linha volta ao carrinho.
    this.limparComposicao();

    if (Number(produto.precoDeVenda ?? 0) > 0) {
      this.carregar(produto);
      return;
    }
    this.precoAusente.emit(produto);
  }

  /**
   * Item lido por LEITOR DE CÓDIGO DE BARRAS: vai DIRETO ao carrinho, pulando a
   * composição — o leitor emite o Enter sozinho, e sem esta exceção seria
   * preciso um segundo Enter por item.
   *
   * Item sem preço continua indo ao modal mesmo bipado: a nota não pode nascer
   * com item a zero, e essa regra não abre exceção.
   */
  onItemBipado(produto: any): void {
    if (!produto?.id) return;

    this.limparComposicao();

    const preco = Number(produto.precoDeVenda ?? 0);
    if (preco <= 0) {
      this.precoAusente.emit(produto);
      return;
    }

    this.lancar.emit({ item: produto, quantidade: 1, valorUnitario: preco });
    // A busca não devolve mais o foco sozinha (`devolverFocoAposEscolher`),
    // então quem reabre a prévia para o próximo bipe somos nós.
    this.focar();
  }

  /** `×` do campo ou texto apagado: desfaz a composição (e restaura a edição). */
  onSelecaoLimpa(): void {
    this.limparComposicao();
  }

  // ===========================================================================
  // Confirmação
  // ===========================================================================

  /**
   * Confirma a composição: manda ao carrinho, esvazia a faixa e devolve o foco
   * ao campo Item.
   *
   * `limparComposicao(false)` — sem restaurar: a edição foi CONCLUÍDA, e a linha
   * antiga não pode voltar (viraria duplicata da que acabou de ser lançada).
   */
  confirmar(): void {
    if (!this.item || this.disabled) return;

    const quantidade = Number(this.qtd) || 0;
    this.lancar.emit({
      item: this.item,
      quantidade: quantidade > 0 ? quantidade : 1,
      valorUnitario: this.precoUnitario,
    });

    this.limparComposicao(false);
    this.focar();
  }

  // ===========================================================================
  // Privados
  // ===========================================================================

  /**
   * Põe o item na faixa e manda o foco para a quantidade, com o valor
   * SELECIONADO — assim digitar "12" substitui o 1 em vez de virar "112".
   *
   * ⚠️ DUPLO setTimeout: é disputa de foco com o PrimeNG, medida em runtime. O
   * `onOptionSelect` emite `onSelect` (cai aqui) e só depois chama `hide()`, que
   * agenda o próprio refoco num `setTimeout(_hide, 0)`
   * (`primeng-autocomplete.mjs:1126-1142`). Com um nível a fila fica [nosso,
   * deles] e o deles vence; com dois, o interno só entra na fila depois do
   * `_hide()`, e a quantidade fica com a última palavra.
   *
   * O nível externo também espera o Angular habilitar o campo, que depende de
   * `item`.
   */
  private carregar(produto: any, quantidade = 1, precoOverride: number | null = null): void {
    this.item = produto;
    this.qtd = quantidade > 0 ? quantidade : 1;
    this.precoOverride = precoOverride;

    // Garante o nome do item no campo. Pelo dropdown o PrimeNG já o escreveu
    // (reescrever o mesmo texto é inofensivo); pela EDIÇÃO não há seleção
    // nenhuma, e sem isto a faixa ficaria preenchida com o campo Item vazio.
    this.busca?.definirTextoSelecionado(produto?.nome ?? '');

    setTimeout(() => {
      setTimeout(() => {
        const input = this.qtdRef?.input?.nativeElement;
        input?.focus();
        input?.select();
      });
    });
  }
}
